import React, { ReactNode, useRef } from 'react';

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';

type Options = {
  noAutoFocus?: boolean;
};

type Props = {
  isOpen: boolean;
  title: string;
  content: string | (() => ReactNode);
  buttons?: Array<string>;
  options?: Options;
  onSubmit?: (button: string, formData: Record<string, unknown>) => void;
  onHide: () => void;
};

export { Props as BasicDialogProps };

export function BasicDialog(props: Props) {
  const {
    isOpen,
    title,
    content,
    buttons = [t('Close')],
    options = {},
    onSubmit,
    onHide,
  } = props;

  const { noAutoFocus } = options;

  const cancelButtonText = buttons.length === 2 ? buttons[0] : undefined;
  const canCancel = cancelButtonText !== undefined;

  const submitButtonText = buttons.slice(-1)[0] ?? '';
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const onCancel = () => {
    if (canCancel) {
      onHide();
      onSubmit?.(cancelButtonText, {});
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      closeOnOverlayClick={canCancel}
      closeOnEsc={canCancel}
      initialFocusRef={noAutoFocus ? undefined : submitButtonRef}
    >
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = event.target;
          const formData = new FormData(
            form instanceof HTMLFormElement ? form : undefined,
          );
          // Note: onHide must be invoked first since onSubmit might try to show another dialog
          onHide();
          onSubmit?.(
            submitButtonText,
            // @ts-expect-error - Will this go away if we upgrade TS?
            Object.fromEntries(formData.entries()),
          );
        }}
      >
        <ModalHeader>
          <Text>{title}</Text>
        </ModalHeader>
        {canCancel ? <ModalCloseButton /> : null}
        <ModalBody pb={6}>
          {typeof content === 'string' ? <Text>{content}</Text> : content()}
        </ModalBody>
        <ModalFooter>
          <HStack>
            {buttons.map((text, index) => {
              const isSubmit = index === buttons.length - 1;
              return isSubmit ? (
                <Button
                  key={index}
                  ref={submitButtonRef}
                  type="submit"
                  colorScheme="blue"
                  variant="solid"
                >
                  {text}
                </Button>
              ) : (
                <Button
                  key={index}
                  onClick={() => {
                    onHide();
                    onSubmit?.(text, {});
                  }}
                >
                  {text}
                </Button>
              );
            })}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
